import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { styled } from '@components-shared/src/stitches';
import { IconButton } from '../icon-buttons/IconButton';

export type Action =
  | { type: 'INCREMENT'; onValueChange?: (value: number) => void }
  | { type: 'DECREMENT'; onValueChange?: (value: number) => void }
  | { type: 'SET_INPUT'; payload: number | ''; onValueChange?: (value: number) => void }
  | { type: 'SET_ERROR' }
  | { type: 'CLEAR_ERROR' };

export type State = { value: number | ''; error: boolean };

//TO DO: https://linear.app/sound/issue/ENG-4202/follow-up-add-test-to-quantityreducer
export function quantityReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'INCREMENT': {
      const newValue = (state.value || 0) + 1;
      action.onValueChange?.(newValue);
      return {
        ...state,
        value: newValue,
      };
    }
    case 'DECREMENT': {
      const newValue = (state.value || 0) - 1;
      action.onValueChange?.(newValue);
      return {
        ...state,
        value: newValue,
      };
    }
    case 'SET_INPUT': {
      if (action.payload !== '') {
        action.onValueChange?.(action.payload);
      }
      return {
        ...state,
        value: action.payload,
      };
    }
    case 'SET_ERROR':
      return {
        ...state,
        error: true,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: false,
      };
  }
}

type QuantityInputProps = {
  /**
   * default min is 1
   */
  min?: number;
  /**
   * default max is 1000
   */
  max?: number;
  /**
   * pass in the state from the reducer
   */
  state: State;
  /**
   * can be used to set the error state on the input
   */
  error?: boolean;
  /**
   * pass in the dispatch from the reducer
   */
  dispatch: React.Dispatch<Action>;
  /**
   * callback when the value changes
   */
  onValueChange?: (value: number) => void;
  /**
   * callback when an error is set or cleared on the input
   */
  onError?: (action: 'SET' | 'CLEAR') => void;
} & React.InputHTMLAttributes<HTMLInputElement>;

/**
 * This component is a controlled input that is used to increment and decrement a quantity
 * You have to pass in the state and dispatch from the reducer
 * You can also pass in a callback to listen to when an error is set or cleared
 */
export const QuantityInput = React.forwardRef<HTMLInputElement, QuantityInputProps>(
  (
    {
      min = 1,
      max = 1000,
      disabled,
      error,
      onChange,
      onValueChange,
      onError,
      state,
      dispatch,
      ...props
    },
    forwardedRef,
  ) => {
    const increment = () => {
      if (state.value && state.value + 1 > max) {
        onError?.('SET');
        return dispatch({
          type: 'SET_ERROR',
        });
      }

      // we had an error previously so we want to clear it up when we incremnet
      if (state.error) {
        onError?.('CLEAR');
        dispatch({
          type: 'CLEAR_ERROR',
        });
      }

      return dispatch({
        type: 'INCREMENT',
        onValueChange,
      });
    };

    const decrement = () => {
      if (state.value && state.value - 1 < min) {
        onError?.('SET');
        return dispatch({
          type: 'SET_ERROR',
        });
      }

      // we had an error previously so we want to clear it up when we decrement
      if (state.error) {
        onError?.('CLEAR');
        dispatch({
          type: 'CLEAR_ERROR',
        });
      }
      return dispatch({
        type: 'DECREMENT',
        onValueChange,
      });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value === '') {
        return dispatch({
          type: 'SET_INPUT',
          payload: '',
        });
      }

      const val = parseInt(e.target.value);
      if (isNaN(val)) {
        onError?.('SET');
        return dispatch({
          type: 'SET_ERROR',
        });
      }

      if (state.error) {
        onError?.('CLEAR');
        dispatch({
          type: 'CLEAR_ERROR',
        });
      }

      return dispatch({
        type: 'SET_INPUT',
        payload: Math.min(Math.max(val, min), max),
        onValueChange,
      });
    };

    const disableButton = disabled || state.error;
    const disableIncrement = disableButton || (state.value || 0) >= max;
    const disableDecrement = disableButton || (state.value || 0) <= min;

    return (
      <div className="flex items-center gap-2">
        <IconButton
          // we don't want to bubble up the click event to the parent in the case that the parent has an onClick
          onClick={e => {
            e.stopPropagation();
            decrement();
          }}
          icon={<FontAwesomeIcon icon={faMinus} size="lg" />}
          variant="secondary"
          disabled={disableDecrement}
          data-testid="QUANTITY_SELECTOR_DECREMENT"
        />
        <NumberInput
          ref={forwardedRef}
          data-state={error || state.error ? 'error' : ''}
          data-testid="QUANTITY_SELECTOR_INPUT"
          inputMode="numeric"
          pattern="[0-9]*"
          type="number"
          min={min}
          max={max}
          value={state.value}
          onChange={e => {
            handleInputChange(e);
            //in the case that consumer has passed in an onChange, we want to call that as well
            onChange?.(e);
          }}
          onBlur={e => {
            if (e.target.value === '') {
              onError?.('SET');
              return dispatch({
                type: 'SET_ERROR',
              });
            }
          }}
          disabled={disabled}
          {...props}
        />
        <IconButton
          onClick={e => {
            // we don't want to bubble up the click event to the parent in the case that the parent has an onClick
            e.stopPropagation();
            increment();
          }}
          icon={<FontAwesomeIcon icon={faPlus} size="lg" />}
          variant="secondary"
          disabled={disableIncrement}
          data-testid="QUANTITY_SELECTOR_INCREMENT"
        />
      </div>
    );
  },
);

QuantityInput.displayName = 'QuantityInput';

const NumberInput = styled('input', {
  display: 'flex',
  backgroundColor: 'transparent',
  outline: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  border: 'none',
  title: 'M',
  width: 60,
  height: 40,
  paddingX: 4,
  borderRadius: 6,

  '&:invalid': {
    backgroundColor: '$destructive50',
  },

  '&:hover, &:focus': {
    backgroundColor: '$base50',
  },

  '&:disabled': {
    opacity: 0.3,
    backgroundColor: 'transparent',

    '&:hover, &:focus': {
      cursor: 'not-allowed',
      backgroundColor: 'transparent',
    },
  },

  '&[data-state="error"]': {
    backgroundColor: '$destructive50',
    '&:hover, &:focus': {
      backgroundColor: '$destructive50',
    },
  },
});
